import { UI_W, UI_H } from '@/engine/canvas';
import type { Game } from './game';
import { text, panel, wrap, dimScreen, drawScaled } from './hud';
import { ITEMS, itemIcon } from './items';
import { T } from './text';
import { FRIENDS } from './roster';
import { journalTitle } from './journal';
import { drawMap } from './map';
import { drawTitleScene } from './title';

/** Key names mean nothing on a phone; describe the on-screen buttons instead. */
const hint = (g: Game, keys: string, touch: string) => g.input.touch ? touch : keys;

export type Phase = 'title' | 'help' | 'select' | 'intro' | 'play' | 'allCaptured' | 'gameover' | 'victory';
export type Overlay = 'none' | 'pause' | 'note' | 'safe' | 'map' | 'help' | 'bag' | 'journal';

export function drawScreens(g: Game, ctx: CanvasRenderingContext2D, ui: CanvasRenderingContext2D): void {
  switch (g.phase) {
    case 'title': drawTitle(g, ctx, ui); return;
    case 'help': drawHelp(g, ui); return;
    case 'select': drawSelect(g, ui); return;
    case 'intro': drawIntro(g, ui); return;
    case 'allCaptured': drawAllCaptured(g, ui); return;
    case 'gameover': drawGameOver(g, ui); return;
    case 'victory': drawVictory(g, ui); return;
  }
  switch (g.overlay) {
    case 'pause': drawPause(g, ui); return;
    case 'note': drawNote(g, ui); return;
    case 'safe': drawSafe(g, ui); return;
    case 'map': drawMap(g, ui); return;
    case 'help': drawHelp(g, ui); return;
    case 'bag': drawBag(g, ui); return;
    case 'journal': drawJournal(g, ui); return;
  }
}

/** Two-column exchange: the bag on the floor and what the character is carrying. */
function drawBag(g: Game, ui: CanvasRenderingContext2D): void {
  dimScreen(ui, 0.72);
  const bag = g.bagTarget;
  if (!bag) return;
  const owner = bag.bag ? g.chars.get(bag.bag) : null;
  const c = g.leader;
  const cap = g.slotCount(c);
  const w = 300, h = 316, gap = 40;
  const x0 = UI_W / 2 - w - gap / 2, x1 = UI_W / 2 + gap / 2, y = 106;
  panel(ui, x0 - 16, 48, w * 2 + gap + 32, 462, '#08060e', '#4a4058');
  text(ui, owner ? `${owner.def.name}의 가방` : '바닥의 물건', UI_W / 2, 66, '#ffe6b8', 'center');
  const column = (x: number, title: string, items: Array<{ kind: import('./items').ItemKind; count: number }>, side: number, limit: string) => {
    const active = g.bagSide === side;
    const first = active ? Math.max(0, g.bagIndex - 7) : 0;
    const range = items.length > 8 ? `${first + 1}–${Math.min(first + 8, items.length)}/${items.length}` : limit;
    panel(ui, x, y, w, h, active ? 'rgba(40,32,20,0.95)' : 'rgba(8,6,14,0.9)', active ? '#ffe6b8' : '#4a4058');
    text(ui, `${title}  ${range}`, x + w / 2, y + 12, active ? '#ffe6b8' : '#8a8a95', 'center');
    if (items.length === 0) text(ui, '비어 있음', x + w / 2, y + h / 2 - 10, '#5a5468', 'center');
    items.slice(first, first + 8).forEach((it, row) => {
      const i = first + row;
      const ry = y + 44 + row * 34;
      const sel = active && i === g.bagIndex;
      if (sel) {
        ui.fillStyle = 'rgba(255,230,184,0.16)';
        ui.fillRect(x + 8, ry - 4, w - 16, 32);
      }
      drawScaled(ui, itemIcon(it.kind), x + 16, ry - 2, 1.75);
      text(ui, ITEMS[it.kind].name + (it.count > 1 ? ` x${it.count}` : ''), x + 52, ry + 2, sel ? '#ffe6b8' : '#d9d0b8');
    });
  };
  column(x0, '가방', bag.contents ?? [], 0, '');
  column(x1, `${c.def.name}의 소지품`, c.inventory, 1, `${c.inventory.length}/${cap}`);
  const cur = g.bagSide === 0 ? (bag.contents ?? [])[g.bagIndex] : c.inventory[g.bagIndex];
  const description = g.bagNotice ?? (cur ? ITEMS[cur.kind].desc : '');
  wrap(ui, description, 632).slice(0, 2).forEach((line, i) => text(ui, line, UI_W / 2, 434 + i * 20, g.bagNotice ? '#ffe6b8' : '#8a8a95', 'center'));
  text(ui, hint(g, '↑↓ 고르기   ←→ 칸 바꾸기   Enter 옮기기   Esc 닫기', '물건을 눌러 고르고 한 번 더 누르면 옮긴다'), UI_W / 2, 486, '#8a8a95', 'center');
}

function drawTitle(g: Game, ctx: CanvasRenderingContext2D, ui: CanvasRenderingContext2D): void {
  const t = g.phaseTimer;
  drawTitleScene(ctx, t);

  // ---- logo: extruded pixel type with a red bleed behind it
  const title = T.title;
  ui.save();
  ui.translate(UI_W / 2, 104);
  ui.font = '16px NeoDGM';
  ui.textAlign = 'center';
  ui.textBaseline = 'middle';
  ui.scale(4, 4);
  const breathe = 0.5 + 0.5 * Math.sin(t * 1.4);
  ui.shadowColor = `rgba(200,50,50,${0.35 + 0.25 * breathe})`;
  ui.shadowBlur = 5 + 3 * breathe;
  ui.fillStyle = 'rgba(120,20,26,0.9)';
  ui.fillText(title, 0, 0);
  ui.shadowBlur = 0; ui.shadowColor = 'transparent';
  for (let d = 3; d >= 1; d--) { ui.fillStyle = d === 1 ? '#5a1c22' : '#2e0c12'; ui.fillText(title, d * 0.5, d * 0.75); }
  const face = ui.createLinearGradient(0, -8, 0, 8);
  face.addColorStop(0, '#fff8ea'); face.addColorStop(0.55, '#f4f0e6'); face.addColorStop(1, '#c9b9a4');
  ui.fillStyle = face;
  ui.fillText(title, 0, 0);
  ui.restore();
  // ornament and tagline
  ui.fillStyle = '#6a5a7a';
  ui.fillRect(UI_W / 2 - 150, 158, 110, 1); ui.fillRect(UI_W / 2 + 40, 158, 110, 1);
  ui.fillRect(UI_W / 2 - 3, 155, 6, 6);
  ui.fillStyle = '#e2b53c'; ui.fillRect(UI_W / 2 - 1, 157, 2, 2);
  text(ui, T.subtitle, UI_W / 2, 172, '#b8b0a0', 'center');

  // ---- menu: a column on the left over the lawn; rows keep their 32px pitch at y=380 (touch taps rely on it)
  const items = g.hasSave ? [T.continueGame, T.newGame, T.howTo] : [T.newGame, T.howTo];
  const mx = 120;
  items.forEach((s, i) => {
    const sel = i === g.menuIndex, y = 380 + i * 32;
    if (sel) {
      const glow = ui.createLinearGradient(mx - 40, 0, mx + 220, 0);
      glow.addColorStop(0, 'rgba(255,230,184,0.16)'); glow.addColorStop(1, 'rgba(255,230,184,0)');
      ui.fillStyle = glow; ui.fillRect(mx - 40, y - 7, 260, 28);
      const pulse = 0.5 + 0.5 * Math.sin(t * 5);
      ui.fillStyle = `rgba(255,230,184,${0.6 + 0.4 * pulse})`;
      ui.fillRect(mx - 22, y + 5, 6, 6);
    }
    text(ui, s, mx, y, sel ? '#ffe6b8' : '#8a8a95', 'left');
  });
  text(ui, hint(g, '↑↓ 선택  Enter 확인', '눌러서 선택'), mx, 380 + items.length * 32 + 14, '#4f4a5e', 'left', false);
  // corners: version and studio
  text(ui, version(), 16, UI_H - 22, '#3e3a4c', 'left', false);
  text(ui, T.developer, UI_W - 16, UI_H - 22, '#3e3a4c', 'right', false);
  // fade in from black
  if (t < 1.4) dimScreen(ui, Math.max(0, 1 - t / 1.4));
}

declare const __APP_VERSION__: string | undefined;
function version(): string {
  return typeof __APP_VERSION__ === 'string' ? `v${__APP_VERSION__}` : 'dev';
}

function drawHelp(g: Game, ui: CanvasRenderingContext2D): void {
  void g;
  dimScreen(ui, 0.75);
  panel(ui, 100, 42, UI_W - 200, UI_H - 84);
  text(ui, '조작법', UI_W / 2, 62, '#ffe6b8', 'center');
  (g.input.touch ? T.touchControls : T.controls).forEach((s, i) => text(ui, s, 128, 104 + i * 26, '#d9d0b8'));
  const tips = [
    '· 손전등 불빛은 괴물 눈에 잘 띈다. 눈이 보이면 불을 끄고 숨어라.',
    '· 달리면 소리가 크다. 괴물이 가까우면 살금살금.',
    '· 잡힌 친구는 괴물이 어딘가에 가둔다. 밖에서 빗장을 풀어 구하라.',
    '· 정문 열쇠를 찾고, 친구를 모두 데리고 현관홀에서 탈출.',
  ];
  tips.forEach((s, i) => text(ui, s, 128, 350 + i * 24, '#a8a0c0'));
  text(ui, hint(g, 'Esc / Enter 닫기', '화면을 눌러 닫기'), UI_W / 2, 470, '#8a8a95', 'center');
}

function drawSelect(g: Game, ui: CanvasRenderingContext2D): void {
  dimScreen(ui, 0.82);
  const cands = g.selectable();
  text(ui, g.runs === 0 ? '누가 먼저 들어갈까?' : '다음은 누가 들어갈까?', UI_W / 2, 60, '#ffe6b8', 'center');
  const captured = g.capturedNames();
  if (captured.length) text(ui, `갇힌 친구: ${captured.join(', ')}`, UI_W / 2, 92, '#c8433d', 'center');
  const w = 100, x0 = UI_W / 2 - (cands.length * w) / 2;
  cands.forEach((fr, i) => {
    const ch = g.chars.get(fr.id)!;
    const sel = i === g.selectIndex % cands.length;
    const x = x0 + i * w, y = 150;
    panel(ui, x + 6, y, w - 12, 120, sel ? 'rgba(60,45,20,0.95)' : 'rgba(8,6,14,0.8)', sel ? '#ffe6b8' : '#4a4058');
    drawScaled(ui, ch.sprites.frames.down.idle, x + w / 2 - 24, y + 18, 2);
    text(ui, fr.name, x + w / 2, y + 96, sel ? '#ffe6b8' : '#d9d0b8', 'center');
  });
  const pick = cands[g.selectIndex % cands.length];
  if (pick) {
    panel(ui, UI_W / 2 - 260, 300, 520, 130);
    text(ui, `${pick.name} · ${pick.trait.name}`, UI_W / 2, 314, '#ffe6b8', 'center');
    text(ui, pick.trait.desc, UI_W / 2, 348, '#d9d0b8', 'center');
    text(ui, pick.line, UI_W / 2, 382, '#8a8a95', 'center');
  }
  text(ui, hint(g, '← → 선택   Enter 들어간다', '친구를 누르고 한 번 더 누르면 들어간다'), UI_W / 2, UI_H - 70, '#8a8a95', 'center');
}

function drawIntro(g: Game, ui: CanvasRenderingContext2D): void {
  const a = Math.min(1, g.phaseTimer / 0.6);
  dimScreen(ui, 0.7 * a);
  const lines = wrap(ui, g.introText, 600);
  lines.forEach((s, i) => text(ui, s, UI_W / 2, 200 + i * 30, `rgba(244,240,230,${a})`, 'center'));
  if (g.phaseTimer > 1.5) text(ui, hint(g, '아무 키나 누르세요', '화면을 누르세요'), UI_W / 2, UI_H - 90, '#8a8a95', 'center');
}

function drawAllCaptured(g: Game, ui: CanvasRenderingContext2D): void {
  const a = Math.min(1, g.phaseTimer / 1.2);
  dimScreen(ui, 0.85 * a);
  text(ui, '모두 잡혔다...', UI_W / 2, UI_H / 2 - 30, `rgba(200,67,61,${a})`, 'center');
  const left = g.selectable().length;
  if (g.phaseTimer > 1.5) text(ui, left ? `밖에서 기다리던 친구 ${left}명이 저택으로 향한다.` : '더는 들어갈 친구가 없다.', UI_W / 2, UI_H / 2 + 20, '#d9d0b8', 'center');
}

function drawGameOver(g: Game, ui: CanvasRenderingContext2D): void {
  dimScreen(ui, 0.9);
  text(ui, '아무도 돌아오지 못했다.', UI_W / 2, UI_H / 2 - 40, '#c8433d', 'center');
  text(ui, `저택은 다시 조용해졌다. (${g.runs}번의 시도)`, UI_W / 2, UI_H / 2, '#8a8a95', 'center');
  if (g.phaseTimer > 2) text(ui, hint(g, '아무 키나 눌러 처음으로', '화면을 눌러 처음으로'), UI_W / 2, UI_H - 90, '#8a8a95', 'center');
}

function drawVictory(g: Game, ui: CanvasRenderingContext2D): void {
  const a = Math.min(1, g.phaseTimer / 1.5);
  dimScreen(ui, 0.85 * a);
  text(ui, '탈출했다!', UI_W / 2, 140, `rgba(255,230,184,${a})`, 'center');
  const escaped = FRIENDS.filter((f) => g.chars.get(f.id)!.state === 'escaped');
  const w = 70, x0 = UI_W / 2 - (escaped.length * w) / 2;
  escaped.forEach((fr, i) => {
    const ch = g.chars.get(fr.id)!;
    drawScaled(ui, ch.sprites.frames.down.idle, x0 + i * w + 5, 200 + Math.round(Math.sin(g.phaseTimer * 4 + i) * 4), 2.5);
    text(ui, fr.name, x0 + i * w + 35, 300, '#d9d0b8', 'center');
  });
  const m = Math.floor(g.endStats.time / 60), s = Math.floor(g.endStats.time % 60);
  text(ui, `걸린 시간 ${m}분 ${s}초 · 구조 시도 ${g.endStats.runs}회 · 잡힌 횟수 ${g.captures}회`, UI_W / 2, 360, '#8a8a95', 'center');
  text(ui, '새벽이 오고 있다. 모두 함께 집으로.', UI_W / 2, 400, '#d9d0b8', 'center');
  if (g.phaseTimer > 3) text(ui, hint(g, '아무 키나 눌러 처음으로', '화면을 눌러 처음으로'), UI_W / 2, UI_H - 90, '#8a8a95', 'center');
}

function drawPause(g: Game, ui: CanvasRenderingContext2D): void {
  dimScreen(ui, 0.6);
  panel(ui, UI_W / 2 - 160, 160, 320, 200);
  text(ui, '일시정지', UI_W / 2, 180, '#ffe6b8', 'center');
  ['계속하기', '조작법', '저장하고 타이틀로'].forEach((s, i) => {
    const sel = i === g.menuIndex;
    text(ui, (sel ? '▶ ' : '   ') + s, UI_W / 2, 230 + i * 34, sel ? '#ffe6b8' : '#8a8a95', 'center');
  });
}

function drawNote(g: Game, ui: CanvasRenderingContext2D): void {
  dimScreen(ui, 0.6);
  const lines = wrap(ui, g.noteText, 520);
  const h = Math.max(160, lines.length * 28 + 80);
  panel(ui, UI_W / 2 - 300, UI_H / 2 - h / 2, 600, h, '#e6dcc0', '#9a8e71');
  lines.forEach((s, i) => text(ui, s, UI_W / 2 - 270, UI_H / 2 - h / 2 + 30 + i * 28, '#3a2f2a', 'left', false));
  text(ui, hint(g, 'E / Esc 닫기', '눌러서 닫기'), UI_W / 2, UI_H / 2 + h / 2 - 34, '#7a6e58', 'center', false);
}

function drawSafe(g: Game, ui: CanvasRenderingContext2D): void {
  dimScreen(ui, 0.6);
  panel(ui, UI_W / 2 - 180, UI_H / 2 - 90, 360, 180);
  text(ui, '금고 다이얼', UI_W / 2, UI_H / 2 - 70, '#ffe6b8', 'center');
  const shown = (g.safeInput + '____').slice(0, 4).split('').join(' ');
  ui.save();
  ui.translate(UI_W / 2, UI_H / 2 - 5);
  ui.scale(2, 2);
  ui.font = '16px NeoDGM';
  ui.textAlign = 'center';
  ui.textBaseline = 'middle';
  ui.fillStyle = '#f4f0e6';
  ui.fillText(shown, 0, 0);
  ui.restore();
  text(ui, hint(g, '숫자 4자리 입력 · Enter 확인 · Esc 취소', '숫자 4자리를 누르고 확인'), UI_W / 2, UI_H / 2 + 50, '#8a8a95', 'center');
}

function drawJournal(g: Game, ui: CanvasRenderingContext2D): void {
  dimScreen(ui, 0.85);
  panel(ui, 32, 36, UI_W - 64, UI_H - 72, 'rgba(15,12,20,0.98)', '#a58b61');
  text(ui, '수첩', 56, 58, '#ffe6b8');
  text(ui, '읽은 단서는 여기에 남는다.', UI_W - 56, 58, '#8a8a95', 'right');
  ui.fillStyle = '#4b4050'; ui.fillRect(308, 94, 1, 348);
  const entries = g.journal;
  const selected = Math.min(g.journalIndex, Math.max(0, entries.length - 1));
  const start = Math.max(0, Math.min(selected - 4, entries.length - 11));
  for (let i = start; i < Math.min(entries.length, start + 11); i++) {
    const y = 106 + (i - start) * 28, active = i === selected;
    if (active) { ui.fillStyle = 'rgba(226,181,60,0.13)'; ui.fillRect(48, y - 5, 246, 26); }
    const title = journalTitle(entries[i]), firstLine = wrap(ui, title, 204)[0];
    text(ui, `${active ? '› ' : '  '}${firstLine}${firstLine.length < title.length ? '…' : ''}`, 56, y, active ? '#ffe6b8' : '#a8a0b0');
  }
  if (entries.length) {
    const entry = entries[selected];
    text(ui, journalTitle(entry), 336, 108, '#ffe6b8');
    ui.fillStyle = '#3a303b'; ui.fillRect(336, 138, 560, 1);
    const body = entry.text.replace(/^\[[^\]]+\]\s*\n?/, '');
    wrap(ui, body, 554).forEach((line, i) => text(ui, line, 336, 158 + i * 24, '#d9d0b8'));
    text(ui, `${selected + 1} / ${entries.length}`, 56, 432, '#81788b');
  } else {
    text(ui, '아직 기록한 단서가 없다.', 336, 154, '#d9d0b8');
    text(ui, '읽은 쪽지와 조사한 단서를 수첩에 옮겨 적는다.', 336, 190, '#8a8a95');
  }
  text(ui, hint(g, '↑↓ / 휠 기록 선택   M 지도   J / Esc 닫기', '기록을 눌러 읽기'), UI_W / 2, 470, '#8a8a95', 'center');
}
